import { useContext, useEffect, useState } from "react";

import {
  DndContext,
  type DragEndEvent,
  PointerSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Save,
  ArrowBack,
  Edit as EditIcon,
  Cancel,
  Add,
  Delete,
  ContentCopy,
  Translate as TranslateIcon,
  Language as LanguageIcon,
  DragIndicator,
  Upgrade as UpgradeIcon,
  TextFormat as TextFormatIcon,
  ArrowDropDown as ArrowDropDownIcon,
  DriveFileMoveOutlined as DriveFileMoveIcon,
  LibraryAddOutlined as LibraryAddIcon,
} from "@mui/icons-material";
import {
  Box,
  Typography,
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Card,
  CardContent,
  Grid,
  TextField,
  IconButton,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import {
  getItemTypeOption,
  putItemTypeOption,
  postItemTypeOption,
  postItemTypeOptionConvertV2,
  postItemTypeOptionRecasePreview,
  getOrgConfigPortal,
  duplicateItemTypeOption,
} from "@rubi/api-generated/clientRs/sdk.gen";
import type {
  Item,
  ItemValue,
  Category,
  CasingStyle,
} from "@rubi/api-generated/clientRs/types.gen";
import { toast } from "@rubi/ui";
import { useParams, useNavigate, useLocation } from "react-router-dom";

import { RUBICON_ADMIN_ORG_IDS } from "@/common/serviceConfigurations";
import { Permission } from "@/components/UserManagement/types";
import { AuthStateContext } from "@/contexts/AuthContext";

import BulkTranslationModal, { ItemTranslation } from "./BulkTranslationModal";
import { CASING_OPTIONS, CASING_LABELS } from "./casingOptions";
import CopyDropdownToOrgModal from "./CopyDropdownToOrgModal";

interface ItemTypeOption {
  id: string;
  name: string;
  rubi_org_id: string;
  items: Item[];
}

// Helper to get available translation languages from items
const getAvailableLanguages = (items: Item[]): string[] => {
  if (!items || items.length === 0) return [];
  const firstItem = items[0];
  if (!firstItem.translations) return [];
  return Object.keys(firstItem.translations).sort();
};

// Helper to extract code from ItemValue
const getValueCode = (value: ItemValue): string => {
  if (typeof value === "string") {
    return value;
  }
  return value.code;
};

// Helper to extract category info
const getCategory = (value: ItemValue, level: 1 | 2 | 3): Category | null => {
  if (typeof value === "string") {
    return null;
  }
  switch (level) {
    case 1:
      return value.category1 || null;
    case 2:
      return value.category2 || null;
    case 3:
      return value.category3 || null;
  }
};

// Helper to create ItemValue from form data
const createItemValue = (
  code: string,
  category1Name?: string,
  category1Code?: string,
  category2Name?: string,
  category2Code?: string,
  category3Name?: string,
  category3Code?: string,
): ItemValue => {
  const hasCategory1 = category1Name && category1Code;
  const hasCategory2 = category2Name && category2Code;
  const hasCategory3 = category3Name && category3Code;

  if (hasCategory1 || hasCategory2 || hasCategory3) {
    return {
      code,
      category1: hasCategory1
        ? { name: category1Name, code: category1Code }
        : undefined,
      category2: hasCategory2
        ? { name: category2Name, code: category2Code }
        : undefined,
      category3: hasCategory3
        ? { name: category3Name, code: category3Code }
        : undefined,
    };
  }
  return code;
};

const DRAG_HANDLE_ACTIVATION_DISTANCE = 8;

interface SortableItemRowProps {
  item: Item & { id: number };
  index: number;
  onEdit: (item: Item, index: number) => void;
  onDuplicate: (index: number) => void;
  onDelete: (index: number) => void;
}

const SortableItemRow = ({
  item,
  index,
  onEdit,
  onDuplicate,
  onDelete,
}: SortableItemRowProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: index });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 2 : 1,
    cursor: isDragging ? "grabbing" : undefined,
    touchAction: "none" as const,
  };

  return (
    <TableRow
      ref={setNodeRef}
      style={style}
      sx={{ bgcolor: isDragging ? "action.hover" : undefined }}
    >
      <TableCell padding="none" sx={{ width: 40, verticalAlign: "middle" }}>
        <Tooltip title="Drag to reorder" placement="right">
          <IconButton
            size="small"
            {...attributes}
            {...listeners}
            sx={{ cursor: "grab" }}
            aria-label="Drag to reorder"
          >
            <DragIndicator color="action" />
          </IconButton>
        </Tooltip>
      </TableCell>
      <TableCell sx={{ minWidth: 150 }}>{item.label || ""}</TableCell>
      <TableCell sx={{ minWidth: 80 }}>{getValueCode(item.value)}</TableCell>
      <TableCell sx={{ minWidth: 120 }}>
        {(() => {
          const cat = getCategory(item.value, 1);
          return cat ? `${cat.name} (${cat.code})` : "";
        })()}
      </TableCell>
      <TableCell sx={{ minWidth: 120 }}>
        {(() => {
          const cat = getCategory(item.value, 2);
          return cat ? `${cat.name} (${cat.code})` : "";
        })()}
      </TableCell>
      <TableCell sx={{ minWidth: 120 }}>
        {(() => {
          const cat = getCategory(item.value, 3);
          return cat ? `${cat.name} (${cat.code})` : "";
        })()}
      </TableCell>
      <TableCell sx={{ minWidth: 160 }}>
        <Box sx={{ display: "flex", gap: 1 }}>
          <IconButton
            size="small"
            onClick={() => onEdit(item, index)}
            aria-label={`Edit ${item.label}`}
          >
            <EditIcon />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => onDuplicate(index)}
            aria-label={`Duplicate ${item.label}`}
          >
            <ContentCopy />
          </IconButton>
          <IconButton
            size="small"
            color="error"
            onClick={() => onDelete(index)}
            aria-label={`Delete ${item.label}`}
          >
            <Delete />
          </IconButton>
        </Box>
      </TableCell>
    </TableRow>
  );
};

const ItemTypeOptionView = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { orgId, listId } = useParams<{ orgId: string; listId: string }>();

  const [itemTypeOption, setItemTypeOption] = useState<ItemTypeOption | null>(
    null,
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editedName, setEditedName] = useState("");
  const [editedItems, setEditedItems] = useState<Item[]>([]);
  const [editingItemIndex, setEditingItemIndex] = useState<number | null>(null);
  const [itemDialogOpen, setItemDialogOpen] = useState(false);
  // Form state for editing an item
  const [currentEditItem, setCurrentEditItem] = useState<{
    label: string;
    code: string;
    category1Name: string;
    category1Code: string;
    category2Name: string;
    category2Code: string;
    category3Name: string;
    category3Code: string;
  }>({
    label: "",
    code: "",
    category1Name: "",
    category1Code: "",
    category2Name: "",
    category2Code: "",
    category3Name: "",
    category3Code: "",
  });
  const [localEditMode, setLocalEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [bulkEditAnchor, setBulkEditAnchor] = useState<null | HTMLElement>(
    null,
  );
  const [itemDialogError, setItemDialogError] = useState<string | null>(null);
  const [isLegacy, setIsLegacy] = useState(false);
  const [converting, setConverting] = useState(false);
  const [convertConfirmOpen, setConvertConfirmOpen] = useState(false);
  const [duplicating, setDuplicating] = useState(false);
  const [privateCopying, setPrivateCopying] = useState(false);
  const [copyOpen, setCopyOpen] = useState(false);

  const { currentUser } = useContext(AuthStateContext);
  // Cross-org copy is open to anyone in a Rubicon admin org — same org-membership gate as the "Viewing as:" switcher. The picker is built from static config, so there is no GET /org call.
  const canCopyToOrg =
    !!currentUser?.rubi_org_id &&
    RUBICON_ADMIN_ORG_IDS.includes(currentUser.rubi_org_id);
  // Copying a list creates a new one, which the backend gates on report_v2.create_survey.
  const canManageLists =
    currentUser?.permissions?.some(
      (p) => p.title === Permission.REPORT_V2_CREATE_SURVEY,
    ) ?? false;

  // Translation-related state
  const [bulkTranslationModalOpen, setBulkTranslationModalOpen] =
    useState(false);
  const [itemTranslations, setItemTranslations] = useState<{
    [itemName: string]: ItemTranslation;
  }>({});
  const [supportedLanguages, setSupportedLanguages] = useState<
    string[] | undefined
  >(undefined);

  const listName = location.state?.listName || "";
  const isReadOnly = location.state?.isReadOnly || false;

  const isUserOrgList = itemTypeOption?.rubi_org_id === orgId;
  const isEditMode =
    !isLegacy &&
    (localEditMode || (location.pathname.includes("/edit") && isUserOrgList));
  const isNewMode = location.pathname.endsWith("/new");

  const app = location.pathname.includes("rubiintel")
    ? "rubiintel"
    : "rubireportv2";

  useEffect(() => {
    if (isNewMode) {
      setItemTypeOption({
        id: "",
        name: "",
        rubi_org_id: orgId || "",
        items: [],
      });
      setEditedName("");
      setEditedItems([]);
      setLoading(false);

      return;
    }

    const fetchData = async () => {
      if (!listId) return;

      try {
        setLoading(true);
        setError(null);

        const response = await getItemTypeOption({
          path: { item_type_option_id: listId },
        });
        const data = response.data as {
          items: { version?: number; items: Item[] };
          isLegacy: boolean;
        };
        const itemsArray = data.items?.items || [];

        const itemTypeOption: ItemTypeOption = {
          id: listId,
          name: listName, // Use the name passed from navigation state
          rubi_org_id: orgId || "",
          items: itemsArray,
        };

        setItemTypeOption(itemTypeOption);
        setEditedName(listName);
        setEditedItems(itemsArray);
        setIsLegacy(!!data.isLegacy);

        // Extract translations from items if they exist
        const extractedTranslations: { [itemName: string]: ItemTranslation } =
          {};
        itemsArray.forEach((item) => {
          if (item.translations && Object.keys(item.translations).length > 0) {
            extractedTranslations[item.label || ""] = item.translations;
          }
        });
        setItemTranslations(extractedTranslations);
      } catch (err) {
        console.error("Failed to fetch item type option:", err);
        setError("Failed to load dropdown list");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [listId, orgId, isNewMode, listName, isReadOnly]);

  // Fetch supported languages from org config
  useEffect(() => {
    async function fetchOrgConfig() {
      if (!orgId) return;
      try {
        const response = await getOrgConfigPortal({
          path: { org_id: orgId },
        });
        const rawLanguages = response.data?.supported_languages;
        if (Array.isArray(rawLanguages)) {
          // Normalize case: backend may return "enCA" but we use "enCa" (camelCase)
          const normalizedLanguages = (rawLanguages as string[]).map((lang) => {
            if (lang === "enCA") return "enCa";
            if (lang === "enUS") return "enUs";
            if (lang === "frCA") return "frCa";
            return lang;
          });
          setSupportedLanguages(normalizedLanguages);
        }
      } catch (error) {
        console.error("Failed to fetch org config:", error);
        // Don't set error state, just fall back to showing all languages
      }
    }
    fetchOrgConfig();
  }, [orgId]);

  const handleDuplicate = async () => {
    if (!listId || !itemTypeOption) return;
    const listDisplayName = itemTypeOption.name || listName || "this list";
    try {
      setDuplicating(true);
      const response = await duplicateItemTypeOption({
        path: { item_type_option_id: listId },
        body: {},
      });
      const newId = response.data?.id;
      const newName = response.data?.name ?? `${listDisplayName} (copy)`;
      toast.success(`Created "${newName}"`);
      if (newId) {
        navigate(`/${app}/${orgId}/forms/dropdowns/${newId}`, {
          state: { listName: newName, isReadOnly: false },
        });
      }
    } catch (err: any) {
      console.error("Failed to duplicate item type option:", err);
      const message =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Failed to duplicate list";
      toast.error(message);
    } finally {
      setDuplicating(false);
    }
  };

  // Copy a shared (or other-org) list into the current org so it can be edited privately.
  const handleMakePrivateCopy = async () => {
    if (!listId || !itemTypeOption || !orgId) return;
    const listDisplayName = itemTypeOption.name || listName || "this list";
    try {
      setPrivateCopying(true);
      // Read the shared list (v2 shape, legacy converted on the fly), then create
      // it under the user's own org.
      const sourceList = await getItemTypeOption({
        path: { item_type_option_id: listId },
      });
      const items = sourceList.data?.items;
      if (!items) throw new Error("Could not load the source list");

      const response = await postItemTypeOption({
        body: {
          rubi_org_id: orgId,
          name: itemTypeOption.name || listName || undefined,
          items,
        },
      });
      const newId = response.data?.item_type_option?.id;
      const newName = response.data?.item_type_option?.name || listDisplayName;
      toast.success(`Copied "${newName}" into your organization`);
      if (newId) {
        navigate(`/${app}/${orgId}/forms/dropdowns/${newId}`, {
          state: { listName: newName, isReadOnly: false },
        });
      }
    } catch (err: any) {
      console.error("Failed to make a private copy:", err);
      const message =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Failed to make a private copy";
      toast.error(message);
    } finally {
      setPrivateCopying(false);
    }
  };

  const handleBack = () => {
    navigate(`/${app}/${orgId}/forms/dropdowns`);
  };

  const handleEdit = () => {
    if (localEditMode || !isUserOrgList || isLegacy) {
      // Already editing, not user's org list (read-only), or legacy v1 (must convert first)
      return;
    }

    // Toggle to edit mode locally
    setLocalEditMode(true);

    // Initialize edited items with current items
    setEditedItems(itemTypeOption?.items || []);
  };

  const handleOpenConvertConfirm = () => {
    if (!listId || !itemTypeOption) return;
    setConvertConfirmOpen(true);
  };

  const handleCloseConvertConfirm = () => {
    if (converting) return;
    setConvertConfirmOpen(false);
  };

  const handleConvertToV2 = async () => {
    if (!listId || !itemTypeOption) return;
    const listDisplayName = itemTypeOption.name || listName || "this list";
    try {
      setConverting(true);
      const response = await postItemTypeOptionConvertV2({
        path: { item_type_option_id: listId },
      });
      const newId = response.data?.id;
      const newName = response.data?.name ?? `${listDisplayName} (v2)`;
      toast.success(`Created v2 copy "${newName}"`);
      setConvertConfirmOpen(false);
      if (newId) {
        navigate(`/${app}/${orgId}/forms/dropdowns/${newId}`, {
          state: { listName: newName, isReadOnly: false },
          replace: true,
        });
      } else {
        navigate(`/${app}/${orgId}/forms/dropdowns`);
      }
    } catch (err: any) {
      console.error("Failed to convert item type option to v2:", err);
      const message =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Failed to convert list to v2";
      toast.error(message);
    } finally {
      setConverting(false);
    }
  };

  const handleSaveWithItems = async (itemsToSave: Item[]) => {
    if (!itemTypeOption || !editedName.trim()) {
      setError("List name is required");
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const requestBody = {
        name: editedName.trim(),
        items: {
          version: 2,
          items: itemsToSave,
        },
        rubi_org_id: orgId!,
      };

      if (isNewMode) {
        // Create new dropdown list
        const response = await postItemTypeOption({
          body: requestBody,
        });

        const newItemTypeOption = response.data!.item_type_option;

        // Extract items from the response (handles both ItemList and legacy array formats)
        const responseItems = Array.isArray(newItemTypeOption.items)
          ? newItemTypeOption.items
          : (newItemTypeOption.items as { items?: Item[] })?.items || [];

        // Update local state with the new item
        setItemTypeOption({
          id: newItemTypeOption.id,
          name: newItemTypeOption.name || "",
          rubi_org_id: newItemTypeOption.rubi_org_id,
          items: responseItems as Item[],
        });

        toast.success("Dropdown list created successfully!");

        // Navigate to view mode of the new item
        navigate(`/${app}/${orgId}/forms/dropdowns/${newItemTypeOption.id}`, {
          state: { listName: newItemTypeOption.name, isReadOnly: false },
          replace: true,
        });
      } else {
        // Update existing dropdown list
        const response = await putItemTypeOption({
          path: {
            item_type_option_id: listId!,
          },
          body: requestBody,
        });

        const updatedItemTypeOption = response.data!.item_type_option;

        // Extract items from the response (handles both ItemList and legacy array formats)
        const responseItems = Array.isArray(updatedItemTypeOption.items)
          ? updatedItemTypeOption.items
          : (updatedItemTypeOption.items as { items?: Item[] })?.items || [];

        // Update local state
        setItemTypeOption({
          ...itemTypeOption,
          name: updatedItemTypeOption.name || "",
          items: responseItems as Item[],
        });

        toast.success("Dropdown list updated successfully!");

        if (localEditMode) {
          // Exit local edit mode and stay on same page
          setLocalEditMode(false);
        } else {
          // Navigate back to view mode
          navigate(`/${app}/${orgId}/forms/dropdowns/${listId}`, {
            state: {
              listName: updatedItemTypeOption.name,
              isReadOnly: isReadOnly,
            },
          });
        }
      }
    } catch (error: any) {
      console.error("Failed to save dropdown list:", error);
      const fallback = isNewMode
        ? "Failed to create dropdown list"
        : "Failed to update dropdown list";
      const errorMsg =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        fallback;
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    // This ensures translations are stored in the items JSON column in the database
    const itemsWithEmbeddedTranslations = editedItems.map((item) => {
      const translations = itemTranslations[item.label || ""];
      if (translations && Object.keys(translations).length > 0) {
        return {
          ...item,
          translations: translations,
        };
      }
      // If no translations, explicitly set to empty object to avoid null values
      return {
        ...item,
        translations: {},
      };
    });

    await handleSaveWithItems(itemsWithEmbeddedTranslations);
  };

  const handleCancel = () => {
    // Reset the edited values to original values
    setEditedName(itemTypeOption?.name || "");
    setEditedItems(itemTypeOption?.items || []);
    // Navigate back to view mode
    if (isNewMode) {
      // If creating new, go back to list
      handleBack();
    } else if (localEditMode) {
      // Exit local edit mode and stay on same page
      setLocalEditMode(false);
    } else {
      // If editing existing, go back to view mode
      navigate(`/${app}/${orgId}/forms/dropdowns/${listId}`, {
        state: { listName: itemTypeOption?.name, isReadOnly: isReadOnly },
      });
    }
  };

  const handleEditItem = (item: Item, index: number) => {
    const category1 = getCategory(item.value, 1);
    const category2 = getCategory(item.value, 2);
    const category3 = getCategory(item.value, 3);
    setCurrentEditItem({
      label: item.label || "",
      code: getValueCode(item.value),
      category1Name: category1?.name || "",
      category1Code: category1?.code || "",
      category2Name: category2?.name || "",
      category2Code: category2?.code || "",
      category3Name: category3?.name || "",
      category3Code: category3?.code || "",
    });
    setEditingItemIndex(index);
    setItemDialogOpen(true);
  };

  const handleAddItem = () => {
    setCurrentEditItem({
      label: "",
      code: "",
      category1Name: "",
      category1Code: "",
      category2Name: "",
      category2Code: "",
      category3Name: "",
      category3Code: "",
    });
    setEditingItemIndex(null);
    setItemDialogOpen(true);
  };

  const handleDeleteItem = (index: number) => {
    const newItems = [...editedItems];
    newItems.splice(index, 1);
    setEditedItems(newItems);
  };

  const handleDuplicateItem = (index: number) => {
    const itemToDuplicate = editedItems[index];
    const duplicatedItem = {
      ...itemToDuplicate,
    };
    const newItems = [...editedItems];
    newItems.splice(index + 1, 0, duplicatedItem);
    setEditedItems(newItems);
  };

  const handleSaveItem = () => {
    // Clear previous errors
    setItemDialogError(null);

    // Validate required fields
    const missingFields: string[] = [];
    if (!currentEditItem.label?.trim()) {
      missingFields.push("Label");
    }
    if (!currentEditItem.code?.trim()) {
      missingFields.push("Code");
    }

    if (missingFields.length > 0) {
      const errorMessage = `Please fill in the following required fields: ${missingFields.join(", ")}`;
      setItemDialogError(errorMessage);
      return;
    }

    const newItems = [...editedItems];
    const itemToSave: Item = {
      label: currentEditItem.label?.trim() || "",
      value: createItemValue(
        currentEditItem.code?.trim() || "",
        currentEditItem.category1Name?.trim(),
        currentEditItem.category1Code?.trim(),
        currentEditItem.category2Name?.trim(),
        currentEditItem.category2Code?.trim(),
        currentEditItem.category3Name?.trim(),
        currentEditItem.category3Code?.trim(),
      ),
      translations: {}, // Initialize with empty translations object
    };

    if (editingItemIndex !== null) {
      // Editing existing item
      newItems[editingItemIndex] = itemToSave;
    } else {
      // Adding new item
      newItems.push(itemToSave);
    }

    setEditedItems(newItems);
    setItemDialogOpen(false);
    setCurrentEditItem({
      label: "",
      code: "",
      category1Name: "",
      category1Code: "",
      category2Name: "",
      category2Code: "",
      category3Name: "",
      category3Code: "",
    });
    setEditingItemIndex(null);
    setItemDialogError(null);
  };

  const handleCancelItemEdit = () => {
    setItemDialogOpen(false);
    setCurrentEditItem({
      label: "",
      code: "",
      category1Name: "",
      category1Code: "",
      category2Name: "",
      category2Code: "",
      category3Name: "",
      category3Code: "",
    });
    setEditingItemIndex(null);
    setItemDialogError(null);
  };

  // Bulk translation handlers
  const handleOpenBulkTranslationModal = () => {
    setBulkTranslationModalOpen(true);
  };

  const handleCloseBulkTranslationModal = () => {
    setBulkTranslationModalOpen(false);
  };

  const handleSaveBulkTranslations = async (bulkTranslations: {
    [itemLabel: string]: ItemTranslation;
  }) => {
    // Replace translations state with saved payload so removed languages are cleared
    const nextTranslations: { [itemLabel: string]: ItemTranslation } = {};
    editedItems.forEach((item) => {
      const label = item.label || "";
      nextTranslations[label] = bulkTranslations[label] ?? {};
    });
    setItemTranslations(nextTranslations);

    // If we're in edit mode, update the edited items and save immediately
    if (isEditMode) {
      // Use bulkTranslations per item; missing or empty means clear translations in DB
      const updatedItems = editedItems.map((item) => ({
        ...item,
        translations: bulkTranslations[item.label || ""] ?? {},
      }));

      setEditedItems(updatedItems);

      // Auto-save to backend with the updated items
      try {
        await handleSaveWithItems(updatedItems);
        toast.success("Bulk translations saved successfully!");
      } catch (error) {
        console.error("Failed to save translations:", error);
        toast.error("Failed to save translations. Please try again.");
      }
    } else {
      toast.success("Bulk translations saved successfully!");
    }

    handleCloseBulkTranslationModal();
  };

  // Re-case every item label to the chosen style via the backend, then load the
  // result into the editor for review. Nothing is persisted until the user Saves.
  const handleApplyCasing = async (casing: CasingStyle) => {
    setBulkEditAnchor(null);
    if (editedItems.length === 0) return;

    // Embed current translations so they survive the round-trip (backend preserves
    // each item's translations and only rewrites the label).
    const itemsWithTranslations = editedItems.map((item) => ({
      ...item,
      translations:
        itemTranslations[item.label || ""] ?? item.translations ?? {},
    }));

    setSaving(true);
    try {
      const response = await postItemTypeOptionRecasePreview({
        body: {
          items: { version: 2, items: itemsWithTranslations },
          casing,
        },
      });

      const recasedItems = response.data!.items.items;
      setEditedItems(recasedItems);

      // Re-key translations by the new (re-cased) labels so they aren't orphaned.
      const nextTranslations: { [itemLabel: string]: ItemTranslation } = {};
      recasedItems.forEach((item) => {
        nextTranslations[item.label || ""] = (item.translations ??
          {}) as ItemTranslation;
      });
      setItemTranslations(nextTranslations);

      toast.success(
        `Applied ${CASING_LABELS[casing]} — review and Save to persist`,
      );
    } catch (error: any) {
      console.error("Failed to apply casing:", error);
      const errorMsg =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        "Failed to apply casing";
      toast.error(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over != null && active.id !== over.id) {
      const oldIndex = Number(active.id);
      const newIndex = Number(over.id);
      if (!Number.isNaN(oldIndex) && !Number.isNaN(newIndex)) {
        setEditedItems((prev) => arrayMove(prev, oldIndex, newIndex));
      }
    }
  };

  const sortableSensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: DRAG_HANDLE_ACTIVATION_DISTANCE },
    }),
    useSensor(TouchSensor),
  );

  const columns: GridColDef[] = [
    {
      field: "label",
      headerName: "Label",
      flex: 1,
      minWidth: 150,
      valueGetter: (value, row) => row.label || "",
    },
    {
      field: "code",
      headerName: "Code",
      flex: 0.6,
      minWidth: 80,
      valueGetter: (value, row) => getValueCode(row.value),
    },
    {
      field: "category1",
      headerName: "Category 1",
      flex: 0.8,
      minWidth: 120,
      valueGetter: (value, row) => {
        const cat = getCategory(row.value, 1);
        return cat ? `${cat.name} (${cat.code})` : "";
      },
    },
    {
      field: "category2",
      headerName: "Category 2",
      flex: 0.8,
      minWidth: 120,
      valueGetter: (value, row) => {
        const cat = getCategory(row.value, 2);
        return cat ? `${cat.name} (${cat.code})` : "";
      },
    },
    {
      field: "category3",
      headerName: "Category 3",
      flex: 0.8,
      minWidth: 120,
      valueGetter: (value, row) => {
        const cat = getCategory(row.value, 3);
        return cat ? `${cat.name} (${cat.code})` : "";
      },
    },
    ...(isEditMode || isNewMode
      ? [
          {
            field: "actions",
            headerName: "Actions",
            flex: 0.5,
            minWidth: 160,
            sortable: false,
            filterable: false,
            renderCell: ({ row, id }) => (
              <Box sx={{ display: "flex", gap: 1 }}>
                <IconButton
                  size="small"
                  onClick={() => handleEditItem(row, id as number)}
                  aria-label={`Edit ${row.item_name}`}
                >
                  <EditIcon />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => handleDuplicateItem(id as number)}
                  aria-label={`Duplicate ${row.item_name}`}
                >
                  <ContentCopy />
                </IconButton>
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => handleDeleteItem(id as number)}
                  aria-label={`Delete ${row.item_name}`}
                >
                  <Delete />
                </IconButton>
              </Box>
            ),
          },
        ]
      : []),
  ];

  // Add unique IDs to items for DataGrid
  const itemsWithIds = (
    isEditMode || isNewMode ? editedItems : itemTypeOption?.items || []
  ).map((item, index) => ({
    ...item,
    id: index, // DataGrid requires unique id field
  }));

  if (loading) {
    return (
      <Box
        sx={{
          p: 3,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "400px",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button onClick={handleBack} startIcon={<ArrowBack />}>
          Back to Dropdown Lists
        </Button>
      </Box>
    );
  }

  if (!itemTypeOption) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          Dropdown list not found
        </Alert>
        <Button onClick={handleBack} startIcon={<ArrowBack />}>
          Back to Dropdown Lists
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: "auto" }}>
      <div id="top-page-div"></div>

      <Button onClick={handleBack} startIcon={<ArrowBack />} sx={{ mb: 3 }}>
        Back to Dropdown Lists
      </Button>

      <Grid container justifyContent="space-between" alignItems="center">
        <Grid size={{ xs: 12, sm: 8 }}>
          <Typography variant="h4" gutterBottom>
            {isNewMode
              ? "Create New Dropdown List"
              : isEditMode
                ? "Edit Dropdown List"
                : "Dropdown List"}
          </Typography>
          {isNewMode || isEditMode ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
              <TextField
                label="List Name"
                value={editedName}
                onChange={(e) => setEditedName(e.target.value)}
                required
                variant="outlined"
                size="medium"
                sx={{ minWidth: 300 }}
              />
            </Box>
          ) : (
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
              <Typography variant="h5" sx={{ fontWeight: 500, color: "#333" }}>
                {itemTypeOption.name}
              </Typography>
              <Tooltip title="Click to copy ID">
                <Chip
                  size="small"
                  icon={<ContentCopy sx={{ fontSize: 14 }} />}
                  label={itemTypeOption.id}
                  onClick={() => {
                    navigator.clipboard.writeText(itemTypeOption.id);
                    toast.success("List ID copied to clipboard");
                  }}
                  sx={{
                    fontFamily: "monospace",
                    cursor: "pointer",
                    "& .MuiChip-label": { fontSize: 12 },
                  }}
                />
              </Tooltip>
            </Box>
          )}
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }} style={{ textAlign: "right" }}>
          {!isNewMode && !isEditMode && isUserOrgList && !isLegacy && (
            <Box
              sx={{
                display: "flex",
                gap: 1.5,
                justifyContent: "flex-end",
                flexWrap: "wrap",
              }}
            >
              <Button
                variant="outlined"
                onClick={handleDuplicate}
                disabled={duplicating}
                startIcon={
                  duplicating ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <ContentCopy />
                  )
                }
              >
                {duplicating ? "Duplicating..." : "Duplicate"}
              </Button>
              {canCopyToOrg && canManageLists && (
                <Button
                  variant="outlined"
                  onClick={() => setCopyOpen(true)}
                  startIcon={<DriveFileMoveIcon />}
                >
                  Copy to org
                </Button>
              )}
              <Button
                variant="contained"
                color="primary"
                onClick={handleEdit}
                startIcon={<EditIcon />}
              >
                Edit
              </Button>
            </Box>
          )}
          {!isNewMode && !isEditMode && isUserOrgList && isLegacy && (
            <Button
              variant="contained"
              color="primary"
              onClick={handleOpenConvertConfirm}
              disabled={converting}
              startIcon={
                converting ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <UpgradeIcon />
                )
              }
            >
              {converting ? "Converting..." : "Convert to v2"}
            </Button>
          )}
          {!isNewMode && !isEditMode && !isUserOrgList && canManageLists && (
            <Button
              variant="contained"
              color="primary"
              onClick={handleMakePrivateCopy}
              disabled={privateCopying}
              startIcon={
                privateCopying ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <LibraryAddIcon />
                )
              }
            >
              {privateCopying ? "Copying..." : "Make a private copy"}
            </Button>
          )}
          {(isEditMode || isNewMode) && (
            <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
              <Button
                variant="outlined"
                onClick={handleCancel}
                startIcon={<Cancel />}
              >
                Cancel
              </Button>
              <Button
                variant="outlined"
                startIcon={<TextFormatIcon />}
                endIcon={<ArrowDropDownIcon />}
                onClick={(e) => setBulkEditAnchor(e.currentTarget)}
                disabled={editedItems.length === 0 || saving}
                aria-haspopup="true"
                aria-expanded={Boolean(bulkEditAnchor)}
              >
                Bulk Edit
              </Button>
              <Menu
                anchorEl={bulkEditAnchor}
                open={Boolean(bulkEditAnchor)}
                onClose={() => setBulkEditAnchor(null)}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ px: 2, py: 0.5, display: "block" }}
                >
                  Set label casing (labels only)
                </Typography>
                {CASING_OPTIONS.map((option) => (
                  <MenuItem
                    key={option.style}
                    onClick={() => handleApplyCasing(option.style)}
                  >
                    <ListItemIcon>
                      <TextFormatIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary={option.label}
                      secondary={`e.g. "${option.example}"`}
                    />
                  </MenuItem>
                ))}
              </Menu>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSave}
                startIcon={
                  saving ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <Save />
                  )
                }
                disabled={saving}
              >
                {saving ? "Saving..." : "Save"}
              </Button>
            </Box>
          )}
        </Grid>
      </Grid>

      {isLegacy && isUserOrgList && (
        <Alert severity="info" sx={{ mt: 2 }}>
          This list is stored in the legacy v1 format and is read-only. Convert
          it to v2 to edit items or translations — the original stays intact so
          any surveys that still reference it keep working.
        </Alert>
      )}

      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Box>
              <Typography variant="h6" gutterBottom>
                Items (
                {
                  (isEditMode || isNewMode ? editedItems : itemTypeOption.items)
                    .length
                }
                )
              </Typography>
              {/* Translation info */}
              {(() => {
                const currentItems =
                  isEditMode || isNewMode ? editedItems : itemTypeOption.items;
                const languages = getAvailableLanguages(currentItems);
                if (languages.length > 0) {
                  return (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mt: -1,
                        mb: 1,
                      }}
                    >
                      <LanguageIcon fontSize="small" color="action" />
                      <Typography variant="body2" color="text.secondary">
                        Translations:
                      </Typography>
                      {languages.map((lang) => (
                        <Tooltip
                          key={lang}
                          title={`Translated to ${lang.toUpperCase()}`}
                        >
                          <Chip
                            label={lang.toUpperCase()}
                            size="small"
                            variant="outlined"
                            color="primary"
                          />
                        </Tooltip>
                      ))}
                    </Box>
                  );
                }
                return (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: -1, mb: 1 }}
                  >
                    No translations available
                  </Typography>
                );
              })()}
            </Box>
            {(isEditMode || isNewMode) && (
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<Add />}
                  onClick={handleAddItem}
                >
                  Add Item
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<TranslateIcon />}
                  onClick={handleOpenBulkTranslationModal}
                  disabled={itemsWithIds.length === 0}
                >
                  Bulk Translate
                </Button>
              </Box>
            )}
          </Box>

          {isEditMode || isNewMode ? (
            <DndContext
              sensors={sortableSensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={editedItems.map((_, i) => i)}
                strategy={verticalListSortingStrategy}
              >
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ width: 40 }} aria-label="Reorder" />
                      <TableCell sx={{ minWidth: 150 }}>Label</TableCell>
                      <TableCell sx={{ minWidth: 80 }}>Code</TableCell>
                      <TableCell sx={{ minWidth: 120 }}>Category 1</TableCell>
                      <TableCell sx={{ minWidth: 120 }}>Category 2</TableCell>
                      <TableCell sx={{ minWidth: 120 }}>Category 3</TableCell>
                      <TableCell sx={{ minWidth: 160 }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {editedItems.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={7}
                          sx={{
                            py: 3,
                            color: "text.secondary",
                            textAlign: "center",
                          }}
                        >
                          No items in this dropdown list yet
                        </TableCell>
                      </TableRow>
                    ) : (
                      editedItems.map((item, index) => (
                        <SortableItemRow
                          key={`${item.label}-${index}`}
                          item={{ ...item, id: index }}
                          index={index}
                          onEdit={handleEditItem}
                          onDuplicate={handleDuplicateItem}
                          onDelete={handleDeleteItem}
                        />
                      ))
                    )}
                  </TableBody>
                </Table>
              </SortableContext>
            </DndContext>
          ) : (
            <DataGrid
              rows={itemsWithIds}
              columns={columns}
              autoHeight
              rowSelection={false}
              disableColumnMenu
              sx={{
                "& .MuiDataGrid-row:not(.MuiDataGrid-row--dynamicHeight)>.MuiDataGrid-cell":
                  {
                    whiteSpace: "normal",
                    wordWrap: "break-word",
                    lineHeight: "inherit",
                    display: "flex",
                    alignItems: "center",
                  },
              }}
              localeText={{
                noRowsLabel: "No items in this dropdown list yet",
              }}
            />
          )}
        </CardContent>
      </Card>

      {/* Item Edit Dialog */}
      <Dialog
        open={itemDialogOpen}
        onClose={handleCancelItemEdit}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingItemIndex !== null ? "Edit Item" : "Add New Item"}
        </DialogTitle>
        <DialogContent>
          {itemDialogError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {itemDialogError}
            </Alert>
          )}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
            <TextField
              label="Label"
              value={currentEditItem.label || ""}
              onChange={(e) => {
                setCurrentEditItem({
                  ...currentEditItem,
                  label: e.target.value,
                });
                if (itemDialogError) setItemDialogError(null);
              }}
              fullWidth
              required
              error={itemDialogError?.includes("Label")}
              helperText="The display text shown to users"
            />
            <TextField
              label="Code"
              value={currentEditItem.code || ""}
              onChange={(e) => {
                setCurrentEditItem({
                  ...currentEditItem,
                  code: e.target.value,
                });
                if (itemDialogError) setItemDialogError(null);
              }}
              fullWidth
              required
              error={itemDialogError?.includes("Code")}
              helperText="Unique identifier for this item"
            />

            {/* Category 1 */}
            <Typography variant="subtitle2" sx={{ mt: 1, mb: -1 }}>
              Category 1 (Optional)
            </Typography>
            <Box sx={{ display: "flex", gap: 2 }}>
              <TextField
                label="Name"
                value={currentEditItem.category1Name || ""}
                onChange={(e) =>
                  setCurrentEditItem({
                    ...currentEditItem,
                    category1Name: e.target.value,
                  })
                }
                fullWidth
                size="small"
              />
              <TextField
                label="Code"
                value={currentEditItem.category1Code || ""}
                onChange={(e) =>
                  setCurrentEditItem({
                    ...currentEditItem,
                    category1Code: e.target.value,
                  })
                }
                fullWidth
                size="small"
              />
            </Box>

            {/* Category 2 */}
            <Typography variant="subtitle2" sx={{ mt: 1, mb: -1 }}>
              Category 2 (Optional)
            </Typography>
            <Box sx={{ display: "flex", gap: 2 }}>
              <TextField
                label="Name"
                value={currentEditItem.category2Name || ""}
                onChange={(e) =>
                  setCurrentEditItem({
                    ...currentEditItem,
                    category2Name: e.target.value,
                  })
                }
                fullWidth
                size="small"
              />
              <TextField
                label="Code"
                value={currentEditItem.category2Code || ""}
                onChange={(e) =>
                  setCurrentEditItem({
                    ...currentEditItem,
                    category2Code: e.target.value,
                  })
                }
                fullWidth
                size="small"
              />
            </Box>

            {/* Category 3 */}
            <Typography variant="subtitle2" sx={{ mt: 1, mb: -1 }}>
              Category 3 (Optional)
            </Typography>
            <Box sx={{ display: "flex", gap: 2 }}>
              <TextField
                label="Name"
                value={currentEditItem.category3Name || ""}
                onChange={(e) =>
                  setCurrentEditItem({
                    ...currentEditItem,
                    category3Name: e.target.value,
                  })
                }
                fullWidth
                size="small"
              />
              <TextField
                label="Code"
                value={currentEditItem.category3Code || ""}
                onChange={(e) =>
                  setCurrentEditItem({
                    ...currentEditItem,
                    category3Code: e.target.value,
                  })
                }
                fullWidth
                size="small"
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelItemEdit}>Cancel</Button>
          <Button onClick={handleSaveItem} variant="contained">
            {editingItemIndex !== null ? "Update" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Translation Modal */}
      <BulkTranslationModal
        open={bulkTranslationModalOpen}
        onClose={handleCloseBulkTranslationModal}
        onSave={handleSaveBulkTranslations}
        items={itemsWithIds.map((item) => ({
          item_name: item.label || "",
          item_code: getValueCode(item.value),
        }))}
        existingTranslations={itemTranslations}
        loading={saving}
        supportedLanguages={supportedLanguages}
      />

      <CopyDropdownToOrgModal
        open={copyOpen}
        onClose={() => setCopyOpen(false)}
        source={
          itemTypeOption
            ? { id: itemTypeOption.id, name: itemTypeOption.name || listName }
            : null
        }
        currentOrgId={orgId ?? ""}
      />

      {/* Convert to v2 Confirmation */}
      <Dialog
        open={convertConfirmOpen}
        onClose={handleCloseConvertConfirm}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Convert to v2?</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 1 }}>
            This will create a new v2 copy of{" "}
            <strong>{itemTypeOption?.name || listName || "this list"}</strong>.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            The original v1 list is kept intact so any surveys still referencing
            it continue to work. You&apos;ll need to swap survey references over
            to the new list manually.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConvertConfirm} disabled={converting}>
            Cancel
          </Button>
          <Button
            onClick={handleConvertToV2}
            variant="contained"
            color="primary"
            disabled={converting}
            startIcon={
              converting ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <UpgradeIcon />
              )
            }
          >
            {converting ? "Converting..." : "Convert to v2"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ItemTypeOptionView;
